<template>
  <span v-if="trackable.includes(parentItem.status)">
    <v-btn-toggle v-model="toggle" dense light rounded>
      <v-btn
        :loading="processingStart"
        :disabled="processingStop || processingComplete"
        x-small
        fab
        color="info"
        v-if="
          parentItem.isCompleted === false &&
          !parentItem.runningStartTime &&
          showStart &&
          hasAccess('timeClock.start')
        "
        @click="startTimer(parentItem)"
      >
        <v-icon>play_arrow</v-icon>
      </v-btn>

      <v-btn
        :disabled="processingStart || processingStop || processingComplete"
        x-small
        fab
        color="error"
        v-else-if="
          parentItem.runningStartTime &&
          parentItem.isCompleted === false &&
          showStop &&
          hasAccess('timeClock.stop')
        "
        @click="stopTimer(parentItem.id)"
      >
        <v-icon>stop</v-icon>
      </v-btn>
      <v-btn
        :disabled="processingStart || processingStop"
        :loading="processingComplete"
        x-small
        fab
        color="success"
        v-if="
          parentItem.isCompleted === false &&
          !parentItem.clockedInCount &&
          hasAccess('timeClock.complete') &&
          showComplete &&
          account.userGroupName !== 'User'
        "
        @click="markComplete(parentItem)"
      >
        <v-icon>check</v-icon>
      </v-btn>
    </v-btn-toggle>

    <ConfirmStop
      :show="showConfirmStop"
      @onClosed="showConfirmStop = false"
      :task="parentItem"
      :timeClockEntry="runningTimeClock"
      @onSubmitted="submitRequest"
    />
  </span>
</template>

<script>
import { accountId } from "src/helpers";
import { mapState, mapActions, mapGetters } from "vuex";
import formatterMixin from "@/mixins/formatterMixin";
import ConfirmStop from "./___StopPopup";

export default {
  components: {
    ConfirmStop,
  },
  mixins: [formatterMixin],
  props: {
    parentItem: Object,
    showStart: {
      type: Boolean,
      default: true
    },
    showStop: {
      type: Boolean,
      default: true
    },
    showComplete: {
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      toggle: "",
      processingStart: false,
      processingStop: false,
      processingComplete: false,
      trackable: ["open", "inprogress"],
      notTrackable: ["canceled", "completed", "invoiced"],
      showConfirmStop: false,
    };
  },
  computed: {
    ...mapState({
      timeclock: (state) => state.timeclock,
      account: (state) => state.account.data.user,
      timeclockFilter: (state) => state.timeclock.tableFilter,
      taskDataRunning: (state) => state.task.dataRunning,
      runningTimeClock: (state) => state.task.runningTimeClock,
    }),

    ...mapGetters("account", ["hasAccess"]),

    isAlreadyRunning() {
      return (
        this.taskDataRunning.filter((item) => {
          return !!item.runningStartTime;
        }).length > 0
      );
    },
  },

  watch: {
    showConfirmStop(n) {
      if(!n) {
        setTimeout(() => {
          this.processingStop = false;
        }, 750);
      }
    }
  },

  methods: {
    ...mapActions("timeclock", [
      "getAll",
      "entryCreate",
      "entryUpdate",
      "update",
    ]),
    ...mapActions("task", ["updateStatus"]),
    ...mapActions({
      getRunningTimeclock: "task/getRunningTimeclock",
      getRunningClockByTaskId: "task/getRunningClockByTaskId",
    }),    
    getCurrentPosition() {
      let support = `geolocation` in navigator;
      if (!support) {
        alert(
          "This browser doesnt support geolocation, please upgrade your browser to latest version!"
        );
        return false;
      }
      let geoSettings = {
        enableHighAccuracy: true,
        maximumAge: 20000,
        timeout: 10000,
      };

      return new Promise(function (resolve, reject) {
        return navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          geoSettings
        );
      });
    },

    async submitRequest(questionnaireTimeclockEntry) {
      const pos = await this.getCurrentPosition()
        .then((pos) => {
          return pos;
        })
        .catch(() => {
          // alert("Getting location fail because " + e.message);
        });

      let data = {
        ...questionnaireTimeclockEntry,
        stopTime: this.momentLocal(),
        stopLatitude: this.optional(pos, "coords.latitude", 0),
        stopLongitude: this.optional(pos, "coords.longitude", 0),
      };
      delete data.jobMaterialsUsed;
      delete data.questionnaire;
      await this.entryUpdate(data);
      await this.getAll({
        ...this.timeclockFilter,
        noLoading: true,
      });
      await this.$emit("onUpdate");
    },

    async stopTimer(taskId) {
      this.processingStop = true;
      await this.getRunningClockByTaskId(taskId);
      this.showConfirmStop = true;
    },

    async startTimer(item) {
      this.processingStart = true;
      await this.startTimerAction(item);
      setTimeout(() => {
        this.processingStart = false;
      }, 1000);
    },

    async startTimerAction(item) {
      await this.getRunningTimeclock({
        isTimeClockRunning: true,
      });

      if (
        this.isAlreadyRunning &&
        !confirm(
          "You already have timeclock running.\nDo you want to start running another timeclock?"
        )
      ) {
        return false;
      }

      const pos = await this.getCurrentPosition()
        .then((pos) => {
          return pos;
        })
        .catch(() => {
          // alert("Getting location fail because " + e.message);
        });

      let data = {
        taskId: item.id,
        employee: this.account.id,
        startTime: this.momentLocal(),
        stopTime: null,
        startLatitude: this.optional(pos, "coords.latitude", 0),
        startLongitude: this.optional(pos, "coords.longitude", 0),
      };

      await this.updateStatus({
        id: item.id,
        status: "inprogress",
      });

      await this.entryCreate(data);

      await this.getAll({
        ...this.timeclockFilter,
        noLoading: true,
      });
      await this.$emit("onUpdate");
    },
    
    async markComplete(item) {
      this.processingComplete = true;
      let data = {
        id: item.id,
        task: {
          ...item,
          accountId: accountId(),
          id: item.id,
          clientId: item.clientId,
          name: item.name,
          description: item.description,
          type: item.type,
          rate: item.rate,
          isCompleted: true,
          isActive: item.isActive,
          dateCompleted: new Date(),
          status: "completed",
        },
        userIds: item.taskUsers.map((user) => user.userId),
        materials: item?.jobMaterials ?? [],
      };
      await this.update(data);
      await this.getAll({
        ...this.timeclockFilter,
        noLoading: true,
      });
      await this.$emit("onUpdate");
      setTimeout(() => {
        this.processingComplete = false;
      }, 1000);
    },
  },
};
</script>